//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, CircularProgress, Grid, InputAdornment, InputLabel, TextField, Typography } from '@mui/material'
import ButtonAction from '@react/components/elements/ButtonAction'
import { poolActions } from '@react/redux/poolSlice'
import {
  commaDecStringToBigNumberWei,
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  erc20WeiToNumber,
  toCommaDecString,
} from '@shared/conversions'
import { PoolDynamicAccountCTO, PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import React, { ReactElement } from 'react'

import { PoolTypeEnum } from '@shared/Enums'

import { useOtDispatch } from '@react/redux/store'
import { redeemRequestFromPoolTx } from '@react/signedTxs/redeemRequestFromPoolTx'
import { useModalForm } from '@react/UseModalForm'
import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'
import { logOT } from '@shared/loggingShared'

export default function LenderRequestWithdrawDynamicForm(props: {
  poolDynamicOverviewCTO: PoolDynamicOverviewCTO
  poolDynamicAccountCTO: PoolDynamicAccountCTO
}): ReactElement {
  const { poolDynamicOverviewCTO, poolDynamicAccountCTO } = props

  const dispatch = useOtDispatch()

  const [isLoading, setIsLoading] = React.useState<boolean>(false)

  const exchangeRateBN = BigInt(poolDynamicOverviewCTO?.exchangeRate ?? 1)
  const liquidityTokenSymbol = poolDynamicOverviewCTO.liquidityTokenSymbol
  const initialUSDCBN = (BigInt(poolDynamicAccountCTO?.maxRedeemRequest) * exchangeRateBN) / BigInt(1e18)

  const initialUSDC = Math.round(Number(initialUSDCBN) / 1e4) / 100

  const [valueUSDC, setValueUSDC] = React.useState<string>(initialUSDC.toLocaleString(navigator.language))
  const [valuePPT, setValuePPT] = React.useState<string>(
    erc20WeiToErc20DecString(poolDynamicAccountCTO?.maxRedeemRequest)
  )
  const [errorUSDC, setErrorUSDC] = React.useState<string | undefined>(undefined)
  const [errorPPT, setErrorPPT] = React.useState<string | undefined>(undefined)
  useModalForm({ poolEventToCloseOn: poolActions.redeemRequestFromPoolSuccess.type })

  const maxPPTBn = BigInt(poolDynamicAccountCTO?.maxRedeemRequest)
  const maxPPTString = erc20WeiToCommaDecString(poolDynamicAccountCTO?.maxRedeemRequest)
  const maxUSDCBn = (BigInt(poolDynamicAccountCTO?.maxRedeemRequest) * exchangeRateBN) / BigInt(1e18)
  const maxUSDCString = erc20WeiToCommaDecString(maxUSDCBn)

  const handleWithdrawRequest = async (): Promise<void> => {
    try {
      setIsLoading(true)
      logOT.debug('LenderRequestWithdrawForm handleWithdrawRequest', valuePPT, valueUSDC)
      await dispatch(
        redeemRequestFromPoolTx({
          poolAddr: poolDynamicOverviewCTO.poolAddr,
          sharesInWei: commaDecStringToErc20Wei(valuePPT),
          poolType: PoolTypeEnum.FlexibleRate,
        })
      )
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    } catch (error) {
      logOT.error('Error in handleWithdrawRequest', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const errorCheck = (ppt: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', ppt)

    const pptInUSDC = (ppt * exchangeRateBN) / BigInt(1e18)

    // Check if amount exceeds max redemption request
    if (ppt > BigInt(poolDynamicAccountCTO?.maxRedeemRequest)) {
      logOT.debug('LenderRequestWithdrawForm Error ', ppt, erc20WeiToNumber(poolDynamicAccountCTO?.maxRedeemRequest))

      setErrorUSDC(
        `Amount exceeds max withdraw request  ${erc20WeiToCommaDecString(
          poolDynamicAccountCTO?.maxWithdrawRequest
        )} ${liquidityTokenSymbol}`
      )
      setErrorPPT(
        `Amount exceeds max redemption request  ${erc20WeiToCommaDecString(poolDynamicAccountCTO?.maxRedeemRequest)} ${
          poolDynamicOverviewCTO?.symbol
        }`
      )
    }
    // Check minimum transfer amount
    else if (
      poolDynamicOverviewCTO?.minimumTransferAmount &&
      pptInUSDC < BigInt(Math.ceil(poolDynamicOverviewCTO.minimumTransferAmount * 1e6))
    ) {
      const minUSDC = BigInt(Math.ceil(poolDynamicOverviewCTO.minimumTransferAmount * 1e6)) // Convert to wei, round up for conservative minimum
      setErrorUSDC(
        `This transaction does not exceed the Minimum Transfer Amount of ${poolDynamicOverviewCTO.minimumTransferAmount.toLocaleString()} ${liquidityTokenSymbol}`
      )

      const minPPT = (minUSDC * BigInt(1e18)) / exchangeRateBN
      setErrorPPT(
        `This transaction does not exceed the Minimum Transfer Amount of ${erc20WeiToCommaDecString(minPPT)} ${poolDynamicOverviewCTO.symbol}`
      )
    }
    // Check maximum transfer amount
    else if (
      poolDynamicOverviewCTO?.maximumTransferAmount &&
      pptInUSDC > BigInt(Math.floor(poolDynamicOverviewCTO.maximumTransferAmount * 1e6))
    ) {
      const maxUSDC = BigInt(Math.floor(poolDynamicOverviewCTO.maximumTransferAmount * 1e6)) // Convert to wei, round down for conservative maximum
      setErrorUSDC(
        `This transaction exceeds the Maximum Transfer Amount of ${poolDynamicOverviewCTO.maximumTransferAmount.toLocaleString()} ${liquidityTokenSymbol}`
      )

      const maxPPT = (maxUSDC * BigInt(1e18)) / exchangeRateBN
      setErrorPPT(
        `This transaction exceeds the Maximum Transfer Amount of ${erc20WeiToCommaDecString(maxPPT)} ${poolDynamicOverviewCTO.symbol}`
      )
    } else {
      setErrorPPT(undefined)
      setErrorUSDC(undefined)
    }
  }

  const handleOnFocusUSDC = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocus', valueUSDC)

    setValueUSDC(toCommaDecString(valueUSDC))

    logOT.info('handleOnFocusUSDC1', valueUSDC)
    const usdc = commaDecStringToBigNumberWei(valueUSDC)

    let ppt
    if (Math.round(Number(usdc) / 10000) == Math.round(Number(maxUSDCBn) / 10000)) {
      ppt = maxPPTBn
    } else {
      ppt = (usdc * BigInt(1e18)) / exchangeRateBN
    }
    logOT.info('handleOnFocusUSDC2', usdc, maxUSDCBn, ppt)

    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(ppt)
  }
  const handleOnBlurUSDC = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valueUSDC)
    setValueUSDC(toCommaDecString(valueUSDC))
  }
  const handleOnChangeUSDC = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)
    setValueUSDC(event.target.value)
    const usdc = textFieldValueToBigNumberWei(event)

    let ppt
    if (Math.round(Number(usdc) / 10000) == Math.round(Number(maxUSDCBn) / 10000)) {
      ppt = maxPPTBn
    } else {
      ppt = (usdc * BigInt(1e18)) / exchangeRateBN
    }

    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(ppt)
  }

  const handleOnFocusPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', valuePPT)
    const ppt = commaDecStringToBigNumberWei(valuePPT)
    setValuePPT(commaDecStringToErcDecString(valuePPT))

    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)
    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(ppt)
  }
  const handleOnBlurPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valuePPT)
    setValuePPT(toCommaDecString(valuePPT))
  }
  const handleOnChangePPT = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setValuePPT(event.target.value)
    const ppt = textFieldValueToBigNumberWei(event)
    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)

    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(ppt)
  }

  const handleMaxClickUSDC = (): void => {
    setValueUSDC(maxUSDCString)
    setValuePPT(maxPPTString)
    errorCheck(maxPPTBn)
  }

  const disableButton = !!errorUSDC || !!errorPPT || isLoading
  logOT.debug('LenderRequestWithdrawForm errorUSDC', disableButton, errorUSDC, errorPPT)

  return (
    <>
      <Box py={2} px={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="bodyBold" margin="dense">
              {` Withdraw Request- ${erc20WeiToCommaDecString(
                poolDynamicAccountCTO?.maxWithdrawRequest
              )} ${liquidityTokenSymbol} available to withdraw`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="bodyBold" margin="dense">
              {`Redeem Request - ${erc20WeiToCommaDecString(poolDynamicAccountCTO?.maxRedeemRequest)} ${
                poolDynamicOverviewCTO.symbol
              } available to Redeem`}
            </Typography>
          </Grid>

          <Grid>
            <Grid item xs={12}>
              <Box sx={{ pl: 6 }}>
                <Typography variant="body" margin={'dense'}>
                  Please input the amount that you would like to withdraw.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="amount">Withdraw Request ({liquidityTokenSymbol})</InputLabel>
            <TextField
              id="amount"
              InputProps={{
                endAdornment: <InputAdornment position="start">{liquidityTokenSymbol}</InputAdornment>,
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valueUSDC}
              type={'tel'}
              onFocus={handleOnFocusUSDC}
              onChange={handleOnChangeUSDC}
              onBlur={handleOnBlurUSDC}
              error={!!errorUSDC}
              helperText={errorUSDC}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  // fontSize: '0.75rem', // Smaller font size
                  padding: '4px 8px', // Adjust padding for smaller size
                  textTransform: 'none', // Prevent uppercase transformation
                }}
                onClick={handleMaxClickUSDC}
              >
                Max
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="ppt">Withdraw Request ({poolDynamicOverviewCTO.symbol})</InputLabel>
            <TextField
              id="ppt"
              InputProps={{
                endAdornment: <InputAdornment position="start">{poolDynamicOverviewCTO.symbol}</InputAdornment>,
              }}
              inputProps={{
                'data-testid': 'ppt',
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valuePPT}
              type={'tel'}
              onFocus={handleOnFocusPPT}
              onChange={handleOnChangePPT}
              onBlur={handleOnBlurPPT}
              error={!!errorPPT}
              helperText={errorPPT}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  // fontSize: '0.75rem', // Smaller font size
                  padding: '4px 8px', // Adjust padding for smaller size
                  textTransform: 'none', // Prevent uppercase transformation
                }}
                onClick={handleMaxClickUSDC}
              >
                Max
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12}>
            <ButtonAction
              data-testid={'WithdrawRequest'}
              disabled={disableButton}
              sx={{ marginRight: '8px', marginLeft: '0px' }}
              variant={'primary'}
              onClick={handleWithdrawRequest}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  <Typography>Processing...</Typography>
                </Box>
              ) : (
                'Withdraw Request'
              )}
            </ButtonAction>

            <ButtonAction variant={'tertiary'} onClick={handleCancel} disabled={isLoading}>
              Cancel
            </ButtonAction>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
